import React, { useState, useEffect } from 'react';
import axiosInstance from '../apis/axiosConfig';
import { toast } from 'react-toastify';
import { FaFileContract, FaSearch, FaTrash } from 'react-icons/fa';
import '../pages_premium.css';
import './HiremateEnquiriesList.css';
import AdminPagination from '../components/common/AdminPagination';

export default function HiremateEnquiriesList() {
    const [enquiries, setEnquiries] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [expandedMsgs, setExpandedMsgs] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const fetchEnquiries = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/hiremate-enquiries/admin');
            setEnquiries(res.data);
        } catch (error) {
            console.error('Error fetching hiremate enquiries:', error);
            toast.error('Failed to load enquiries');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEnquiries();
    }, []);

    const deleteEnquiry = async (id) => {
        if (!window.confirm('Are you sure you want to delete this enquiry?')) return;
        try {
            await axiosInstance.delete(`/hiremate-enquiries/${id}`);
            toast.success('Enquiry deleted successfully');
            fetchEnquiries();
        } catch (error) {
            console.error('Error deleting enquiry:', error);
            toast.error('Failed to delete enquiry');
        }
    };

    const filtered = enquiries.filter(e =>
        e.name?.toLowerCase().includes(search.toLowerCase()) ||
        e.email?.toLowerCase().includes(search.toLowerCase()) ||
        e.Hiremate?.service_title?.toLowerCase().includes(search.toLowerCase()) ||
        e.Provider?.name?.toLowerCase().includes(search.toLowerCase())
    );

    const paginatedEnquiries = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const getStatusClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'new': return 'status-new';
            case 'contacted': return 'status-contacted';
            case 'resolved': return 'status-resolved';
            default: return 'status-default';
        }
    };

    return (
        <div className="hiremate-enquiries-list-page page-premium-wrapper">
            <div className="page-premium-content">

                {/* Hero Header */}
                <div className="page-hero-header">
                    <div className="ph-left">
                        <div className="ph-icon">
                            <FaFileContract />
                        </div>
                        <div>
                            <h1 className="ph-title">HireMate Enquiries</h1>
                            <p className="ph-subtitle">Manage enquiries and applications submitted by users for HireMate services</p>
                        </div>
                    </div>

                    <div className="ph-right">
                        <div className="ph-count-badge">
                            <span className="count-num">{enquiries.length}</span>
                            <span className="count-lbl">Total Enquiries</span>
                        </div>
                    </div>
                </div>

                {/* Table Card */}
                <div className="premium-table-card">
                    <div className="ptable-toolbar">
                        <div className="ptable-title">
                            <span className="ptable-title-dot"></span>
                            Enquiries List
                        </div>
                        <div className="ptable-search">
                            <FaSearch className="ptable-search-icon" />
                            <input
                                type="text"
                                placeholder="Search by client, service, provider..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="ptable-wrap">
                        {loading ? (
                            <div className="text-center py-5 text-secondary">Loading enquiries...</div>
                        ) : (
                            <table className="premium-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Client Name</th>
                                        <th>Contact Info</th>
                                        <th>Service Requested</th>
                                        <th>Service Provider</th>
                                        <th>Message</th>
                                        <th>Status</th>
                                        <th className="text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedEnquiries.map((enq) => (
                                        <tr key={enq.id}>
                                            <td style={{ color: '#64748b', fontWeight: 600 }}>#{enq.id}</td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <span className="ptable-avatar">
                                                        {enq.name ? enq.name.charAt(0).toUpperCase() : 'E'}
                                                    </span>
                                                    <span style={{ fontWeight: 600, color: '#0f172a' }}>{enq.name}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ fontWeight: 500, color: '#1e293b' }}>{enq.email}</div>
                                                <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{enq.phone || 'No phone'}</div>
                                            </td>
                                            <td>
                                                <span style={{ fontWeight: 600, textTransform: 'capitalize', color: '#0f172a' }}>
                                                    {enq.Hiremate?.service_title || 'Service Listing'}
                                                </span>
                                            </td>
                                            <td>
                                                <div style={{ fontWeight: 500, color: '#334155', textTransform: 'capitalize' }}>
                                                    {enq.Provider?.name || 'N/A'}
                                                </div>
                                                <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{enq.Provider?.email}</div>
                                            </td>
                                            <td>
                                                <div style={{ maxWidth: '260px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                    {enq.message && enq.message.length > 85 ? (
                                                        <>
                                                            {expandedMsgs[enq.id] ? enq.message : `${enq.message.substring(0, 85)}...`}
                                                            <button
                                                                onClick={() => setExpandedMsgs({ ...expandedMsgs, [enq.id]: !expandedMsgs[enq.id] })}
                                                                className="btn btn-link p-0 d-block font-weight-bold mt-1"
                                                                style={{ fontSize: '11px', textDecoration: 'none', color: '#f97316' }}
                                                            >
                                                                {expandedMsgs[enq.id] ? 'Show Less' : 'Show More'}
                                                            </button>
                                                        </>
                                                    ) : (
                                                        enq.message || '-'
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`status-badge ${getStatusClass(enq.status)}`}>
                                                    {enq.status || 'new'}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <div className="action-btn-group">
                                                    <button
                                                        onClick={() => deleteEnquiry(enq.id)}
                                                        className="icon-action-btn icon-action-btn-delete"
                                                        title="Delete Enquiry"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {filtered.length === 0 && (
                                        <tr>
                                            <td colSpan="8" className="text-center text-muted py-5">
                                                No enquiries found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                    <AdminPagination
                        currentPage={currentPage}
                        totalItems={filtered.length}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={setPageSize}
                        itemLabel="enquiries"
                    />
                </div>

            </div>
        </div>
    );
}
